import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, Send, X, Loader2, Trash2, 
  Bot, User, ChevronDown, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HelpAssistant = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Add welcome message on first load
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm your StreakFree Help Assistant. I can help you navigate the CRM and explain how to do anything. Try asking me:\n\n• How do I add a new customer?\n• Where can I see my invoices?\n• How do I assign jobs to workers?\n• How do I view reports?"
      }]);
    }
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        const error = await response.json();
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Sorry, I encountered an error: ${error.detail || 'Please try again.'}` 
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Sorry, I couldn't connect to the server. Please check your connection and try again." 
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        const token = localStorage.getItem('streakfree_token');
        await fetch(`${API_URL}/api/assistant/chat/${sessionId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error clearing chat:', error);
      }
    }
    setMessages([{
      role: 'assistant',
      content: "Chat cleared! How can I help you today?"
    }]);
    setSessionId(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Quick action buttons
  const quickActions = [
    "How do I add a customer?",
    "Where are my invoices?",
    "How to assign jobs?"
  ];

  if (isMinimized) {
    return (
      <div className="h-full flex flex-col bg-white">
        <button
          onClick={() => setIsMinimized(false)}
          className="flex items-center justify-center gap-2 p-4 hover:bg-emerald-50 transition-colors"
          data-testid="expand-assistant"
        >
          <Bot size={20} className="text-emerald-600" />
          <span className="text-sm font-medium text-zinc-700">Open Help Assistant</span>
          <ChevronDown size={16} className="text-zinc-400" />
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="help-assistant">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <Bot size={18} className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">Help Assistant</h3>
            <p className="text-emerald-100 text-xs">Ask me anything about StreakFree</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clearChat}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            title="Clear chat"
            data-testid="clear-chat"
          >
            <Trash2 size={16} className="text-white/80" />
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            title="Minimize"
            data-testid="minimize-assistant"
          >
            <X size={16} className="text-white/80" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
          >
            <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${
              msg.role === 'user' 
                ? 'bg-emerald-500 text-white' 
                : 'bg-zinc-200 text-zinc-600'
            }`}>
              {msg.role === 'user' ? <User size={14} /> : <Bot size={14} />}
            </div>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
              msg.role === 'user'
                ? 'bg-emerald-500 text-white'
                : 'bg-white border border-zinc-200 text-zinc-700'
            }`}>
              <p className="whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex gap-2">
            <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center">
              <Bot size={14} className="text-zinc-600" />
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl px-3 py-2">
              <Loader2 size={16} className="animate-spin text-emerald-500" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions (show only at start) */}
      {messages.length <= 1 && (
        <div className="px-3 pb-2">
          <p className="text-xs text-zinc-500 mb-2">Quick questions:</p>
          <div className="flex flex-wrap gap-1">
            {quickActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setInput(action);
                  setTimeout(() => sendMessage(), 100);
                }}
                className="text-xs bg-white border border-zinc-200 px-2 py-1 rounded-full hover:bg-emerald-50 hover:border-emerald-200 transition-colors"
                data-testid={`quick-action-${idx}`}
              >
                {action}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-3 bg-white border-t border-zinc-200">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything..."
            className="flex-1 px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            disabled={isLoading}
            data-testid="chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            className="bg-emerald-500 hover:bg-emerald-600 px-3"
            data-testid="send-message"
          >
            {isLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HelpAssistant;
